import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetDeployRuntimeLogs } from "@/hooks/useDeploys";
import { ApplicationLogEntry, Deploy } from "@/types";
import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";

interface RuntimeLogsViewerProps {
  deploy: Deploy;
}

type DisplayLog = {
  id?: string;
  level: string;
  message: string;
  stackTrace?: string;
  requestId?: string | null;
  ingestionSource?: string | null;
  timestamp?: string;
};

const LOG_LEVEL_OPTIONS = [
  { value: "ALL", label: "All levels" },
  { value: "DEBUG", label: "Debug" },
  { value: "INFO", label: "Info" },
  { value: "WARN", label: "Warn" },
  { value: "ERROR", label: "Error" },
  { value: "FATAL", label: "Fatal" },
] as const;

type LogLevelOption = (typeof LOG_LEVEL_OPTIONS)[number]["value"];

const TIME_RANGE_PRESETS = {
  "5m": { label: "5m", durationMs: 5 * 60 * 1000 },
  "30m": { label: "30m", durationMs: 30 * 60 * 1000 },
  "1h": { label: "1h", durationMs: 60 * 60 * 1000 },
  "4h": { label: "4h", durationMs: 4 * 60 * 60 * 1000 },
  "8h": { label: "8h", durationMs: 8 * 60 * 60 * 1000 },
  "24h": { label: "24h", durationMs: 24 * 60 * 60 * 1000 },
  "7d": { label: "7d", durationMs: 7 * 24 * 60 * 60 * 1000 },
} as const;

type TimeRangeKey = keyof typeof TIME_RANGE_PRESETS;

const DEFAULT_TIME_RANGE: TimeRangeKey = "4h";
const RUNTIME_LOGS_PAGE_SIZE = 100;

// Preserving global styling
const panelClass =
  "rounded-2xl border border-slate-800/80 bg-slate-950/60 shadow-sm";
const panelHeaderClass =
  "flex flex-col gap-1 border-b border-slate-800/70 px-5 py-4";
const panelBodyClass = "px-0 py-0"; // Removed padding for full-width table look

const computeTimeRange = (option: TimeRangeKey) => {
  const preset = TIME_RANGE_PRESETS[option];
  const end = new Date();
  return {
    start: new Date(end.getTime() - preset.durationMs),
    end,
  };
};

type RuntimeLogWithRawTimestamps = ApplicationLogEntry & {
  createdAt?: string;
  created_at?: string;
  logged_at?: string;
};

const normalizeTimestamp = (value: unknown): string | undefined => {
  if (value == null) return undefined;
  if (typeof value === "number") {
    const milliseconds = value > 1e12 ? value : value * 1000;
    const date = new Date(milliseconds);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
  }
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const getRuntimeLogTimestamp = (
  log: RuntimeLogWithRawTimestamps,
): string | undefined => {
  return (
    normalizeTimestamp(log.loggedAt) ??
    normalizeTimestamp(log.createdAt) ??
    normalizeTimestamp(log.logged_at) ??
    normalizeTimestamp(log.created_at)
  );
};

const formatTime = (timestamp?: string) => {
  if (!timestamp) return "--:--:--";
  return new Date(timestamp).toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

export function RuntimeLogsViewer({ deploy }: RuntimeLogsViewerProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedLevel, setSelectedLevel] = useState<LogLevelOption>("ALL");
  const [timeRangeOption, setTimeRangeOption] =
    useState<TimeRangeKey>(DEFAULT_TIME_RANGE);
  const [timeRange, setTimeRange] = useState(() =>
    computeTimeRange(DEFAULT_TIME_RANGE),
  );
  const [page, setPage] = useState(0);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    setTimeRangeOption(DEFAULT_TIME_RANGE);
    setSelectedLevel("ALL");
    setTimeRange(computeTimeRange(DEFAULT_TIME_RANGE));
    setPage(0);
    setDebouncedSearch("");
    setSearchTerm("");
  }, [deploy.id]);

  useEffect(() => {
    setTimeRange(computeTimeRange(timeRangeOption));
  }, [timeRangeOption]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, selectedLevel, timeRangeOption]);

  const levelFilter = selectedLevel === "ALL" ? undefined : selectedLevel;
  const searchFilter = debouncedSearch || undefined;
  const offset = page * RUNTIME_LOGS_PAGE_SIZE;

  const runtimeLogsQuery = useGetDeployRuntimeLogs(deploy.id, {
    startTime: timeRange.start.getTime(),
    endTime: timeRange.end.getTime(),
    limit: RUNTIME_LOGS_PAGE_SIZE,
    offset,
    level: levelFilter,
    search: searchFilter,
  });

  const runtimeLogs: DisplayLog[] = useMemo(() => {
    const items = runtimeLogsQuery.data?.items ?? [];
    return items
      .map((log) => {
        const runtimeLog = log as RuntimeLogWithRawTimestamps;
        const timestamp = getRuntimeLogTimestamp(runtimeLog);

        let payload: Record<string, unknown> | undefined;
        if (log.payload) {
          if (typeof log.payload === "string") {
            try {
              payload = JSON.parse(log.payload);
            } catch {
              // Ignore parse errors, payload will remain undefined
            }
          } else {
            payload = log.payload as Record<string, unknown>;
          }
        }

        let stackTrace: string | undefined;
        if (payload && typeof payload === "object") {
          const stack = payload.stack;
          const stackTraceField = payload.stackTrace;
          const errorObj = payload.error as Record<string, unknown> | undefined;

          if (typeof stack === "string") {
            stackTrace = stack;
          } else if (typeof stackTraceField === "string") {
            stackTrace = stackTraceField;
          } else if (errorObj && typeof errorObj.stack === "string") {
            stackTrace = errorObj.stack;
          }
        }

        return {
          id: log.id,
          timestamp,
          level: (log.level ?? "info").toLowerCase(),
          message: log.message ?? "",
          stackTrace,
          requestId: log.requestId ?? undefined,
          ingestionSource: log.ingestionSource ?? undefined,
        } satisfies DisplayLog;
      })
      .sort((a, b) => {
        const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return aTime - bTime;
      });
  }, [runtimeLogsQuery.data]);

  const handleRefresh = () => {
    setTimeRange(computeTimeRange(timeRangeOption));
    setPage(0);
    runtimeLogsQuery.refetch();
  };

  const getLevelStyles = (level: string) => {
    switch (level) {
      case "error":
      case "fatal":
        return "text-rose-400 font-bold";
      case "warn":
      case "warning":
        return "text-amber-400 font-bold";
      case "debug":
        return "text-indigo-400";
      default:
        return "text-slate-400";
    }
  };

  return (
    <div className="space-y-6">
      <Card className={panelClass}>
        <CardHeader className={panelHeaderClass}>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle className="text-sm font-semibold text-slate-100">
                Runtime Logs
              </CardTitle>
              <CardDescription className="text-xs text-slate-400">
                Live application output and exceptions.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {runtimeLogsQuery.isFetching && (
                <span className="text-xs text-slate-500 animate-pulse mr-2">
                  Syncing...
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                className="h-8 border-slate-800 bg-slate-900/50 text-xs text-slate-300 hover:text-white"
                onClick={handleRefresh}
                disabled={runtimeLogsQuery.isFetching}
              >
                Refresh
              </Button>
            </div>
          </div>

          {/* Toolbar */}
          <div className="flex flex-col gap-3 pt-4 md:flex-row">
            <div className="flex-1">
              <Input
                placeholder="Filter logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-9 bg-slate-950/50 border-slate-800 text-slate-200 placeholder:text-slate-600 focus-visible:ring-0 focus-visible:border-slate-700 font-mono text-xs"
              />
            </div>
            <div className="flex gap-2">
              <Select
                value={selectedLevel}
                onValueChange={(value) =>
                  setSelectedLevel(value as LogLevelOption)
                }
              >
                <SelectTrigger className="w-[110px] h-9 bg-slate-950/50 border-slate-800 text-xs text-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-950 border-slate-800 text-slate-200">
                  {LOG_LEVEL_OPTIONS.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      className="text-xs"
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={timeRangeOption}
                onValueChange={(value) =>
                  setTimeRangeOption(value as TimeRangeKey)
                }
              >
                <SelectTrigger className="w-[80px] h-9 bg-slate-950/50 border-slate-800 text-xs text-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-950 border-slate-800 text-slate-200">
                  {Object.entries(TIME_RANGE_PRESETS).map(([key, preset]) => (
                    <SelectItem key={key} value={key} className="text-xs">
                      {preset.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent className={panelBodyClass}>
          <div className="min-h-[300px] w-full overflow-x-auto">
            <div className="min-w-[800px] text-xs font-mono">
              {/* Table Header */}
              <div className="flex border-b border-slate-800/50 bg-slate-950/30 px-4 py-2 text-slate-500 font-semibold select-none">
                <div className="w-[140px] shrink-0">Timestamp</div>
                <div className="w-[60px] shrink-0">Level</div>
                <div className="flex-1">Message</div>
                <div className="w-[120px] shrink-0 text-right">Request ID</div>
              </div>

              {/* Empty / Loading States */}
              {!runtimeLogsQuery.isFetching && runtimeLogs.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-slate-500">
                  <p>No logs found in this range.</p>
                </div>
              )}

              {/* Log Rows */}
              <div className="divide-y divide-slate-800/30">
                {runtimeLogs.map((log, index) => (
                  <div
                    key={log.id ?? index}
                    className="group flex px-4 py-2 hover:bg-slate-900/40 transition-colors items-start"
                  >
                    <div className="w-[140px] shrink-0 text-slate-500 select-text">
                      {formatTime(log.timestamp)}
                    </div>
                    <div
                      className={cn(
                        "w-[60px] shrink-0 uppercase text-[10px] pt-0.5",
                        getLevelStyles(log.level),
                      )}
                    >
                      {log.level}
                    </div>
                    <div className="flex-1 min-w-0 pr-4">
                      <p className="text-slate-300 break-words whitespace-pre-wrap leading-relaxed">
                        {log.message}
                      </p>
                      {log.stackTrace && (
                        <div className="mt-2 rounded bg-rose-950/10 border-l-2 border-rose-800 p-2 overflow-x-auto">
                          <pre className="text-rose-200/70 text-[10px] leading-relaxed">
                            {log.stackTrace}
                          </pre>
                        </div>
                      )}
                    </div>
                    <div
                      className="w-[120px] shrink-0 text-right text-slate-600 truncate select-all"
                      title={log.requestId || ""}
                    >
                      {log.requestId || "-"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer Pagination */}
          <div className="flex items-center justify-between border-t border-slate-800/70 bg-slate-950/30 px-4 py-3">
            <span className="text-xs text-slate-500">Page {page + 1}</span>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-slate-400 hover:text-slate-100 disabled:opacity-30"
                onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
                disabled={page === 0 || runtimeLogsQuery.isFetching}
              >
                Previous
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-slate-400 hover:text-slate-100 disabled:opacity-30"
                onClick={() => setPage((prev) => prev + 1)}
                disabled={
                  !runtimeLogsQuery.data?.hasNext || runtimeLogsQuery.isFetching
                }
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
